import { useState } from "react";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { memberApi, useMemberMutation, errorMessage, useMyConsents } from "@/hooks/useMember";

function Row({ k, v }) {
  return <div className="flex justify-between gap-6 border-b border-forest/10 py-3 text-sm"><dt className="text-charcoal/55">{k}</dt><dd className="text-right text-charcoal">{v}</dd></div>;
}

export default function SettingsPanel() {
  const { user } = useAuth();
  const { data: consents } = useMyConsents();
  const [deletionRequested, setDeletionRequested] = useState(false);
  const update = useMemberMutation(memberApi.updateConsents, () => toast.success("Permissions updated."));

  const exportData = async () => {
    try {
      const data = await memberApi.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "rituals-with-you-my-data.json"; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Your data export has been downloaded.");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const requestDeletion = async () => {
    if (!window.confirm("Request deletion of your account and data? ELA will confirm by email before anything is removed.")) return;
    try { await memberApi.requestDeletion(); setDeletionRequested(true); toast.success("Deletion request received."); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="settings-panel">
      <div className="border border-forest/15 bg-cream p-7">
        <h3 className="font-serif font-light text-2xl text-forest">Your details</h3>
        <dl className="mt-4">
          <Row k="Display name" v={user?.display_name || user?.name || "—"} />
          <Row k="Email" v={user?.email} />
          <Row k="Skin type" v={user?.skin_type || "Not set"} />
          <Row k="Interests" v={(user?.interests || []).join(", ") || "Not set"} />
        </dl>
      </div>
      <div className="space-y-8">
        <div className="border border-forest/15 bg-cream p-7">
          <h3 className="font-serif font-light text-2xl text-forest">Permissions</h3>
          <p className="mt-2 text-sm text-charcoal/60 font-light">Optional, never on by default. Change them any time; each change is recorded with a timestamp.</p>
          <div className="mt-5 space-y-5">
            <label className="flex items-center justify-between gap-4 text-sm text-charcoal/80">
              <span>Occasional emails about programmes and the journal</span>
              <Switch checked={!!consents?.marketing_consent} onCheckedChange={(v) => update.mutate({ marketing_consent: v })} data-testid="consent-marketing" />
            </label>
            <label className="flex items-center justify-between gap-4 text-sm text-charcoal/80">
              <span>Permission to feature my shared stories in ELA media</span>
              <Switch checked={!!consents?.media_consent} onCheckedChange={(v) => update.mutate({ media_consent: v })} data-testid="consent-media" />
            </label>
          </div>
        </div>
        <div className="border border-forest/15 bg-cream p-7">
          <h3 className="font-serif font-light text-2xl text-forest">Your data</h3>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button onClick={exportData} className="btn btn-outline" data-testid="export-data-button"><Download size={14} /> Export my data</button>
            <button onClick={requestDeletion} disabled={deletionRequested} className="btn btn-outline disabled:opacity-50" data-testid="request-deletion-button"><Trash2 size={14} /> {deletionRequested ? "Deletion requested" : "Request deletion"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
